import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import type { DefaultValues, FieldValues } from "react-hook-form";

import FieldsWatcher from "../FieldsWatcher/FieldsWatchers";
import FormError from "../FormError/FormError";
import FormLoading from "../FormLoading/FormLoading";

type SimpleFormProviderProps<T extends FieldValues> = {
  readonly children: React.ReactNode;
  readonly onSubmit: (data: T) => Promise<unknown>;
  readonly onReset?: () => void;
  readonly onSuccess?: (data: T, resp: unknown) => void;
  readonly onChange?: (data: T) => void;
  readonly defaultValues?: DefaultValues<T>;
  readonly resetFields?: boolean;
  readonly schema: any;
  readonly successMessage?: string;
  readonly hideLoading?: boolean;
  readonly autoSubmit?: boolean;
  /** Debounces the onChange handler AND autosubmit. */
  readonly onChangeDebounce?: number;
  readonly onValidationErrors?: (errors: any) => void;
  readonly onError?: (err: any) => void;
  readonly fullScreenLoader?: boolean;
};

export default function SimpleFormProvider<T extends FieldValues>({
  children,
  onSubmit,
  onSuccess,
  onReset,
  onChange,
  defaultValues = {} as DefaultValues<T>,
  resetFields = true,
  schema,
  hideLoading = false,
  autoSubmit = false,
  onChangeDebounce = 500,
  successMessage,
  onValidationErrors,
  onError,
  fullScreenLoader = false,
}: SimpleFormProviderProps<T>): React.ReactElement {
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<T>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  const handleError = (err: any) => {
    console.error("Submission error:", err);

    const error = err as any;

    const code = error.response?.data?.title;

    let message = error.message;

    if (code) {
      message = `${error.message} (${code}) `;
    }

    message = message || "An unexpected error occurred";

    methods.setError("root.server", {
      type: "server",
      message,
    });

    onError?.(err);
  };

  const handleSuccess = (payload: T, resp: unknown) => {
    onSuccess?.(payload, resp);

    if (resetFields) {
      methods.reset({ ...defaultValues });
      onReset?.();
    }
  };

  const handleFieldsChanged = async (data: T) => {
    onChange?.(data);

    if (autoSubmit && !isLoading) {
      methods.clearErrors("root.server");
      const isValid = await methods.trigger();
      if (isValid) {
        setIsLoading(true);
        submitHandler(data);
      }
    }
  };

  const submitHandler = async (data: T) => {
    if (isLoading) {
      return null;
    }

    setIsLoading(true);

    methods.clearErrors("root");

    try {
      const resp = await onSubmit(data);

      handleSuccess(data, resp);
    } catch (err) {
      handleError(err);
    }

    setIsLoading(false);
  };

  const errorHandler = (errors: any) => {
    console.log("errors", errors);
    onValidationErrors?.(errors);
  };

  const handleInternalSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return methods.handleSubmit(submitHandler, errorHandler)(e);
  };

  useEffect(() => {
    if (resetFields) {
      methods.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <FormProvider {...methods}>
      <form className="grid gap-2" onSubmit={handleInternalSubmit}>
        {children}
        <FormError />
        {!hideLoading && <FormLoading fullScreen={fullScreenLoader} />}
        <FieldsWatcher
          debounce={onChangeDebounce}
          onChange={handleFieldsChanged}
        />
      </form>
    </FormProvider>
  );
}
